// pages/portal/ProfilePage.js —— 门户「个人中心」：修改姓名 / 密码 / 手机号 / 邮箱
// 依据《云平台账号权限》手册个人中心模块，界面为轮廓级演示（不做后端对接）。
// 说明：各表单的校验、验证码倒计时、保存均在前端本地完成并回写 localStorage，用于呈现实交互；后端接口待接入。
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined, UserOutlined, PhoneOutlined, MailOutlined,
  LockOutlined, SafetyOutlined, CheckCircleFilled, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { PORTAL_COMPANY_LIST } from '../../config/stationData';

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const saved = readUser();

  const [base, setBase] = useState({
    name: saved.name || '平台用户',
    dept: '技术部',
    role: '管理员',
  });
  const [phone, setPhone] = useState(saved.phone || '13800004270');
  const [email, setEmail] = useState(saved.email || '');
  const [phoneCode, setPhoneCode] = useState('');
  const [pwd, setPwd] = useState({ old: '', next: '', confirm: '' });
  const [msg, setMsg] = useState(null); // { text, type }
  const [phoneCd, setPhoneCd] = useState(0);
  const [mailCd, setMailCd] = useState(0);

  const timer = useRef(null);
  const toast = (text, type = 'ok') => {
    setMsg({ text, type });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 2800);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    if (phoneCd <= 0) return undefined;
    const t = setTimeout(() => setPhoneCd((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneCd]);
  useEffect(() => {
    if (mailCd <= 0) return undefined;
    const t = setTimeout(() => setMailCd((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [mailCd]);

  const company = localStorage.getItem('currentCompany') || PORTAL_COMPANY_LIST[0].name;
  const account = saved.account || saved.username || 'admin';

  // ---------- 基本信息 ----------
  const saveBase = () => {
    if (!base.name.trim()) { toast('请输入姓名', 'error'); return; }
    localStorage.setItem('currentUser', JSON.stringify({ ...saved, name: base.name.trim() }));
    toast('已保存基本信息');
  };

  // ---------- 手机号 ----------
  const sendPhoneCode = () => {
    if (!/^1\d{10}$/.test(phone.trim())) { toast('请输入 11 位手机号', 'error'); return; }
    setPhoneCd(60);
    toast('验证码已发送：签名为【储能管家】的 4 位数字验证码（可输入任意 4 位数字）');
  };
  const changePhone = () => {
    if (!/^1\d{10}$/.test(phone.trim())) { toast('请输入 11 位手机号', 'error'); return; }
    if (!/^\d{4}$/.test(phoneCode.trim())) { toast('请输入 4 位数字验证码', 'error'); return; }
    localStorage.setItem('currentUser', JSON.stringify({ ...saved, phone: phone.trim() }));
    setPhoneCode('');
    toast('手机号已更换');
  };

  // ---------- 邮箱 ----------
  const sendMailCode = () => {
    if (!/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email.trim())) { toast('请输入正确的邮箱地址', 'error'); return; }
    setMailCd(60);
    toast('验证邮件已发送至邮箱');
  };
  const saveEmail = () => {
    if (!/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email.trim())) { toast('请输入正确的邮箱地址', 'error'); return; }
    localStorage.setItem('currentUser', JSON.stringify({ ...saved, email: email.trim() }));
    toast(email.trim() ? '邮箱已保存' : '邮箱已解绑');
  };

  // ---------- 密码 ----------
  const changePwd = () => {
    if (!pwd.old) { toast('请输入原密码', 'error'); return; }
    if (!/^(?=.*[A-Za-z])(?=.*\d).{8,20}$/.test(pwd.next)) { toast('新密码需 8-20 位且同时包含字母与数字', 'error'); return; }
    if (pwd.next !== pwd.confirm) { toast('两次输入的新密码不一致', 'error'); return; }
    setPwd({ old: '', next: '', confirm: '' });
    toast('密码已修改，请使用新密码登录');
  };

  return (
    <div className="pp-page">
      <div className="pp-header">
        <div className="pp-header-left">
          <button type="button" className="pp-back" onClick={() => navigate('/portal')}>
            <ArrowLeftOutlined /> 返回门户
          </button>
          <span className="pp-title">个人中心</span>
        </div>
        <div className="pp-header-right">
          <span className="pp-company">{company}</span>
          <img src="/logo.png" alt="logo" />
        </div>
      </div>

      {msg ? <div className={`pp-toast${msg.type === 'error' ? ' error' : ''}`}>{msg.text}</div> : null}

      <div className="pp-body">
        <aside className="pp-aside">
          <div className="pp-profile">
            <div className="pp-avatar"><UserOutlined /></div>
            <div className="pp-profile-name">{base.name}</div>
            <div className="pp-profile-account">账号：{account}</div>
            <div className="pp-profile-tags">
              <span className="pp-tag primary">{base.role}</span>
              <span className="pp-tag">{base.dept}</span>
            </div>
          </div>

          <div className="pp-security">
            <div className="pp-security-title"><SafetyOutlined /> 账号安全状态</div>
            <div className="pp-security-item ok">
              <CheckCircleFilled /> 手机号已绑定
            </div>
            <div className={`pp-security-item${email ? ' ok' : ' warn'}`}>
              {email ? <CheckCircleFilled /> : <ExclamationCircleOutlined />}
              {email ? '邮箱已绑定' : '邮箱未绑定'}
            </div>
            <div className="pp-security-item ok">
              <CheckCircleFilled /> 密码已设置
            </div>
          </div>
        </aside>

        <main className="pp-main">
          <section className="pp-card">
            <div className="pp-card-head">
              <h3><UserOutlined /> 基本信息</h3>
              <small>用于平台内的称呼与归属展示</small>
            </div>
            <div className="pp-field">
              <label className="pp-label">姓名</label>
              <input className="pp-input" value={base.name} placeholder="请输入真实姓名或工作昵称" onChange={(e) => setBase({ ...base, name: e.target.value })} />
            </div>
            <div className="pp-field">
              <label className="pp-label">登录账号</label>
              <input className="pp-input" value={account} disabled />
              <span className="pp-field-tip">登录账号由平台分配，不可修改。</span>
            </div>
            <div className="pp-field">
              <label className="pp-label">所属部门</label>
              <input className="pp-input" value={base.dept} disabled />
            </div>
            <div className="pp-actions">
              <button type="button" className="pp-btn primary" onClick={saveBase}>保存修改</button>
            </div>
          </section>

          <section className="pp-card">
            <div className="pp-card-head">
              <h3><PhoneOutlined /> 手机号</h3>
              <small>用于验证码登录与安全验证</small>
            </div>
            <div className="pp-field">
              <label className="pp-label">手机号</label>
              <div className="pp-inline">
                <input className="pp-input" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} maxLength={11} placeholder="请输入手机号" />
                <button type="button" className="pp-btn ghost" disabled={phoneCd > 0} onClick={sendPhoneCode}>
                  {phoneCd > 0 ? `${phoneCd}s 后重发` : '获取验证码'}
                </button>
              </div>
            </div>
            <div className="pp-field">
              <label className="pp-label">验证码</label>
              <input className="pp-input" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))} placeholder="请输入 4 位验证码" maxLength={4} />
            </div>
            <div className="pp-actions">
              <button type="button" className="pp-btn primary" onClick={changePhone}>更换手机号</button>
            </div>
          </section>

          <section className="pp-card">
            <div className="pp-card-head">
              <h3><MailOutlined /> 邮箱</h3>
              <small>用于邮箱登录与通知接收</small>
            </div>
            <div className="pp-field">
              <label className="pp-label">邮箱</label>
              <div className="pp-inline">
                <input className="pp-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="请输入邮箱" />
                <button type="button" className="pp-btn ghost" disabled={mailCd > 0} onClick={sendMailCode}>
                  {mailCd > 0 ? `${mailCd}s 后重发` : '发送验证'}
                </button>
              </div>
            </div>
            <div className="pp-actions">
              <button type="button" className="pp-btn primary" onClick={saveEmail}>保存邮箱</button>
            </div>
          </section>

          <section className="pp-card">
            <div className="pp-card-head">
              <h3><LockOutlined /> 修改密码</h3>
              <small>修改后需使用新密码重新登录</small>
            </div>
            <div className="pp-field">
              <label className="pp-label">原密码</label>
              <input className="pp-input" type="password" value={pwd.old} onChange={(e) => setPwd({ ...pwd, old: e.target.value })} placeholder="请输入原密码" />
            </div>
            <div className="pp-field">
              <label className="pp-label">新密码</label>
              <input className="pp-input" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} placeholder="8-20 位，含字母与数字" />
            </div>
            <div className="pp-field">
              <label className="pp-label">确认新密码</label>
              <input className="pp-input" type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} placeholder="请再次输入新密码" />
            </div>
            <div className="pp-actions">
              <button type="button" className="pp-btn primary" onClick={changePwd}>确认修改</button>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;